import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import TablePagesTitle from "../../components/table-pages-title/TablePagesTitle";
import privateAxios from "../../utils/axiosPrivate";
import { getJobQueueHealthUrl } from "../../utils/constants/apiEndPoints";
import { showToast } from "../../utils/utils";
import {
  showToastType,
  JOB_QUEUE_HEALTH_POLL_INTERVAL,
  JOB_QUEUE_HEALTH_MAX_POLL_INTERVAL,
  VISIBILITY_CHANGE_EVENT
} from "../../utils/constants/appConstants";
import { jobQueueHealth } from "../../locales/en";
import { commonErrMsg } from "../../locales/en";
import { CLIENT_DETAILS } from "../../route/paths";
import "../../assets/scss/common.scss";
import "./JobQueueHealth.scss";

/** "125" seconds -> "2m 5s"; under a minute -> "45s". */
const formatAgeSeconds = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined) {
    return jobQueueHealth?.none;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

/** True for the error axios/fetch raise when a request is aborted via AbortController. */
const isAbortError = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

/** Doubles the wait per consecutive failure (15s, 30s, 60s, 120s...), capped. */
const nextPollDelay = (consecutiveFailures) => {
  if (consecutiveFailures <= 0) {
    return JOB_QUEUE_HEALTH_POLL_INTERVAL;
  }
  const backedOff = JOB_QUEUE_HEALTH_POLL_INTERVAL * 2 ** consecutiveFailures;
  return Math.min(backedOff, JOB_QUEUE_HEALTH_MAX_POLL_INTERVAL);
};

const JobQueueHealth = () => {
  const [health, setHealth] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);

  // Returns { failed } so the poll loop can back off on repeated failures.
  // Aborts any still-in-flight request first (unmount, or a slow previous
  // fetch overtaken by a visibility-triggered refresh) so responses can never
  // land out of order and clobber a fresher one with a stale one.
  const fetchHealth = useCallback(async (isBackgroundRefresh) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isBackgroundRefresh) {
      setIsRefreshing(true);
    }
    try {
      const response = await privateAxios.get(getJobQueueHealthUrl, {
        signal: controller.signal
      });
      setHealth(response?.data?.data || null);
      consecutiveFailuresRef.current = 0;
      return { failed: false };
    } catch (error) {
      if (isAbortError(error)) {
        // Superseded or unmounted — not a real failure, don't toast or back off.
        return { failed: false };
      }
      // Only toast on the first failure of a run, not on every backed-off retry —
      // otherwise an extended outage spams a toast every 15-120s indefinitely.
      if (consecutiveFailuresRef.current === 0) {
        showToast(
          jobQueueHealth?.errors?.fetchFailed || commonErrMsg?.item7,
          showToastType.error
        );
      }
      consecutiveFailuresRef.current += 1;
      return { failed: true };
    } finally {
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const scheduleNext = (delayMs) => {
      timeoutRef.current = setTimeout(poll, delayMs);
    };

    // Recursive setTimeout, not setInterval: the next poll is only scheduled
    // once this one finishes, so a slow response (the exact condition this
    // page exists to surface) can never cause overlapping in-flight requests.
    const poll = async () => {
      if (document.hidden) {
        // Paused while the tab isn't visible; still re-checks on the base
        // interval so it resumes on its own without needing the
        // visibilitychange listener below.
        scheduleNext(JOB_QUEUE_HEALTH_POLL_INTERVAL);
        return;
      }
      const { failed } = await fetchHealth(true);
      if (cancelled) {
        return;
      }
      scheduleNext(nextPollDelay(failed ? consecutiveFailuresRef.current : 0));
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab just came back — refresh now rather than waiting out whatever
        // delay (possibly a long backoff one) was already pending.
        clearTimeout(timeoutRef.current);
        poll();
      }
    };

    fetchHealth(false).then(() => {
      if (!cancelled) {
        scheduleNext(JOB_QUEUE_HEALTH_POLL_INTERVAL);
      }
    });
    document.addEventListener(VISIBILITY_CHANGE_EVENT, handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      document.removeEventListener(
        VISIBILITY_CHANGE_EVENT,
        handleVisibilityChange
      );
      abortControllerRef.current?.abort();
    };
  }, [fetchHealth]);

  const statusEntries = Object.entries(health?.inFlightCountsByStatus || {});
  const customerEntries = health?.inFlightCountsByCustomer || [];

  return (
    <>
      <TablePagesTitle
        mainTitle={jobQueueHealth?.mainTitle}
        content={jobQueueHealth?.subheading}
      />

      <div className="row mb-3 mx-0">
        <div className="col-12">
          {initialLoading ? (
            <div className="job-queue-health-card">
              <p className="job-queue-health-loading">
                {jobQueueHealth?.refreshing}
              </p>
            </div>
          ) : (
            <>
              <div className="job-queue-health-stats">
                <div className="job-queue-health-stat">
                  <span className="stat-label">
                    {jobQueueHealth?.queueDepth}
                  </span>
                  <span className="stat-value">{health?.queueDepth ?? 0}</span>
                </div>
                <div className="job-queue-health-stat">
                  <span className="stat-label">
                    {jobQueueHealth?.activeWorkers}
                  </span>
                  <span className="stat-value">
                    {health?.activeWorkerCount ?? 0}
                  </span>
                </div>
                <div className="job-queue-health-stat">
                  <span className="stat-label">{jobQueueHealth?.poolSize}</span>
                  <span className="stat-value">{health?.poolSize ?? 0}</span>
                </div>
                <div className="job-queue-health-stat">
                  <span className="stat-label">
                    {jobQueueHealth?.maxPoolSize}
                  </span>
                  <span className="stat-value">{health?.maxPoolSize ?? 0}</span>
                </div>
                <div className="job-queue-health-stat">
                  <span className="stat-label">
                    {jobQueueHealth?.oldestInFlightJob}
                  </span>
                  <span className="stat-value">
                    {formatAgeSeconds(health?.oldestInFlightJobAgeSeconds)}
                  </span>
                </div>
              </div>

              {isRefreshing && (
                <p className="job-queue-health-refreshing-note">
                  {jobQueueHealth?.refreshing}
                </p>
              )}

              <div className="job-queue-health-breakdowns">
                <div className="job-queue-health-breakdown">
                  <h5>{jobQueueHealth?.byStatus}</h5>
                  {statusEntries.length === 0 ? (
                    <p className="no-data-note">
                      {jobQueueHealth?.noJobsInFlight}
                    </p>
                  ) : (
                    <table className="job-queue-health-table">
                      <thead>
                        <tr>
                          <th>{jobQueueHealth?.statusColumn}</th>
                          <th>{jobQueueHealth?.countColumn}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusEntries.map(([status, count]) => (
                          <tr key={status}>
                            <td>{status}</td>
                            <td>{count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="job-queue-health-breakdown">
                  <h5>{jobQueueHealth?.byCustomer}</h5>
                  {customerEntries.length === 0 ? (
                    <p className="no-data-note">
                      {jobQueueHealth?.noJobsInFlight}
                    </p>
                  ) : (
                    <table className="job-queue-health-table">
                      <thead>
                        <tr>
                          <th>{jobQueueHealth?.customerColumn}</th>
                          <th>{jobQueueHealth?.countColumn}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerEntries.map(
                          ({ ownerId, customerName, count }) => (
                            <tr key={ownerId}>
                              <td>
                                <Link to={CLIENT_DETAILS(ownerId)}>
                                  {customerName}
                                </Link>{" "}
                                (#{ownerId})
                              </td>
                              <td>{count}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default JobQueueHealth;
